import { useState } from 'react';
import { usePayInstallment } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { Field, Modal, MoneyInput } from '@/components/ui';
import { IconCheck } from '@/components/Icons';
import { formatBRL, formatDate, nowHHMM, todayISO } from '@/lib/format';
import { brlFromRate, formatCrypto } from '@/lib/crypto';
import type { CryptoInstallment, CryptoSale } from '@/lib/types';

interface PayInstallmentModalProps {
  sale: CryptoSale;
  installment: CryptoInstallment;
  onClose: () => void;
}

/** "5,42" ou "5.42" → 5.42; vazio ou inválido → 0. */
const parseRate = (raw: string): number => {
  const value = Number(raw.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : 0;
};

/**
 * Marcar a parcela como paga: o dia, quanto entrou em reais (pela cotação do
 * dia ou digitado) e a referência do saque. Vira um comprovante da venda.
 */
export function PayInstallmentModal({ sale, installment, onClose }: PayInstallmentModalProps) {
  const toast = useToast();
  const pay = usePayInstallment();
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState(nowHHMM());
  const [rate, setRate] = useState('');
  const [paidBrl, setPaidBrl] = useState(0);
  const [txRef, setTxRef] = useState('');
  const [error, setError] = useState<string | null>(null);

  function changeRate(raw: string) {
    setRate(raw);
    const parsed = parseRate(raw);
    if (parsed > 0) setPaidBrl(brlFromRate(installment.amount, parsed));
  }

  async function submit() {
    if (!date) return setError('Informe o dia do pagamento.');
    if (paidBrl <= 0) return setError('Informe quanto entrou em reais — pela cotação do dia ou o valor direto.');
    setError(null);
    try {
      await pay.mutateAsync({ saleId: sale.id, installmentId: installment.id, date, time, paidBrl, ...(txRef.trim() ? { txRef: txRef.trim() } : {}) });
      toast.success(`Parcela ${installment.number} paga: ${formatBRL(paidBrl)} no vendido.`);
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <Modal
      title={`Parcela ${installment.number} de ${sale.installments.length} paga`}
      width={520}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={pay.isPending}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={pay.isPending}>
            {pay.isPending ? <span className="spinner" /> : <IconCheck />} Marcar como paga
          </button>
        </>
      }
    >
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        {sale.description} · <span className="num">{formatCrypto(installment.amount, sale.currency)}</span> · vencimento{' '}
        <span className="num">{formatDate(installment.dueDate)}</span>
      </p>

      <div className="grid grid2">
        <Field label="Dia do pagamento *">
          <input className="inp" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label="Hora">
          <input className="inp" type="time" value={time} onChange={(event) => setTime(event.target.value)} />
        </Field>
      </div>

      <div className="grid grid2">
        <Field label={`Cotação (R$ por 1 ${sale.currency === 'USD' ? 'dólar' : sale.currency})`} hint="Preenche o valor em reais.">
          <input
            className="inp num"
            inputMode="decimal"
            placeholder="Ex.: 5,42"
            value={rate}
            onChange={(event) => changeRate(event.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Entrou em reais (R$) *" hint="É o que conta no vendido e na comissão.">
          <MoneyInput value={paidBrl} onChange={setPaidBrl} onEnter={() => void submit()} />
        </Field>
      </div>

      <Field label="Hash ou referência do saque">
        <input
          className="inp"
          value={txRef}
          onChange={(event) => setTxRef(event.target.value)}
          placeholder="Opcional — ajuda a conferir depois"
          maxLength={200}
        />
      </Field>

      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
    </Modal>
  );
}
